import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { earlyRegistrationService } from '@/services/earlyRegistrationService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import { Calendar, Phone, Mail, User, BookOpen, Home } from 'lucide-react'

export default function EarlyRegistrationsPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [serviceFilter, setServiceFilter] = useState('') // '' means all

  const { data, isLoading, isError } = useQuery({
    queryKey: ['earlyRegistrations', page, serviceFilter],
    queryFn: () => earlyRegistrationService.getRegistrations({ page, limit: 10, service: serviceFilter || undefined }),
    keepPreviousData: true
  })

  const registrations = data?.data || []
  const meta = data?.meta || {}

  const getServiceBadge = (service) => {
    if (service === 'kids-learning') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
          <BookOpen className="w-4 h-4" /> عالم الأطفال
        </span>
      )
    }
    if (service === 'monthly-needs') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          <Home className="w-4 h-4" /> اشتراك المنزل
        </span>
      )
    }
    return service
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">طلبات التسجيل المبكر</h1>
          <p className="text-sm text-gray-500 mt-1">تصفح وإدارة طلبات التسجيل للخدمات الجديدة</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 w-full sm:w-auto">
          <select 
            value={serviceFilter}
            onChange={(e) => { setServiceFilter(e.target.value); setPage(1) }}
            className="input max-w-xs"
          >
            <option value="">جميع الخدمات</option>
            <option value="kids-learning">عالم الأطفال</option>
            <option value="monthly-needs">اشتراك المنزل الشهري</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center bg-white rounded-2xl border border-gray-100">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <EmptyState title="حدث خطأ" description="فشل في تحميل البيانات" />
      ) : registrations.length === 0 ? (
        <EmptyState 
          title="لا توجد تسجيلات" 
          description="لم يقم أحد بالتسجيل المبكر حتى الآن في هذا القسم." 
          icon={Calendar}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-right">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-4 text-sm font-semibold text-gray-900">الاسم</th>
                  <th className="px-6 py-4 text-sm font-semibold text-gray-900">الخدمة</th>
                  <th className="px-6 py-4 text-sm font-semibold text-gray-900">عمر الطفل</th>
                  <th className="px-6 py-4 text-sm font-semibold text-gray-900">تاريخ التسجيل</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {registrations.map(reg => (
                  <tr key={reg.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        {reg.parentName}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-col gap-1">
                        <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {reg.email}</span>
                        <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> <span dir="ltr">{reg.phone}</span></span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getServiceBadge(reg.service)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {reg.childAge ? reg.childAge : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(reg.createdAt).toLocaleDateString('en-GB')}
                      <br/>
                      <span className="text-xs">{new Date(reg.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {meta.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                صفحة {meta.page} من {meta.totalPages}
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
                >
                  السابق
                </button>
                <button 
                  onClick={() => setPage(p => Math.min(meta.totalPages, p + 1))}
                  disabled={page === meta.totalPages}
                  className="btn-outline px-3 py-1 text-sm disabled:opacity-50"
                >
                  التالي
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
